// ── usePositionPnl — Shared P&L calculation hook ────────────────
// Used by both PositionTracker and ValueDashboard's PositionSidebar.
// Derives unrealized P&L from accountStore positions + marketStore prices.

import { useMemo } from 'react';
import { feeDisplayStatus, feeFor, useFeeStore } from '../../services/hydraFees';
import {
  describeUnpricedReasons,
  summarizeUnrealizedCoverage,
  type UnpricedReason,
} from '../../lib/accounting/unrealizedCoverage';
import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';
import { useThrottledMarketVersion } from './useThrottledMarketVersion';
import { estimateAggregateFeeCents, takerRateForTicker } from '../../lib/pricing/fees';

export interface PositionWithPnl {
  ticker: string;
  side: 'yes' | 'no';
  position: number;
  averagePrice: number;
  /** Canonical most-recent fill for this aggregate, when the source proves it. */
  lastFillAt?: string | null;
  /** Exact entry basis in cents from the relay's fills ledger (V2 org-key
   *  path; carried through from the store position by the `...pos` spread).
   *  Display math should prefer costBasisCents / position over the
   *  whole-cent-rounded averagePrice — re-deriving odds from the rounded
   *  avg shifts the line (63.62¢ reads -174.9; rounded 64¢ reads -177.8). */
  costBasisCents?: number;
  /** Lifetime bought / sold contracts for the bucket — the full-position
   *  anchor (#91), carried through from the store position by the `...pos`
   *  spread. `position` is the post-sale remainder, so original qty and its
   *  cost/to-win must come from these instead. */
  totalBoughtCount?: number;
  totalSoldCount?: number;
  /** Lifetime buy cost in cents — the original entry basis, un-rounded.
   *  Fee-exclusive (see the relay's OpenPosition jsdoc). */
  totalBuyCostCents?: number;
  currentPrice: number | null;
  feesPaidCents?: number;
  /** Entry cost + fees actually paid so far. */
  totalCostCents: number;
  /** Mark-to-market (current × qty). Null when current price unknown.
   *  Gross notional — does NOT account for sell-side fees. Use
   *  `cashOutCents` when displaying what the user would actually receive
   *  on a market sell. */
  totalValueCents: number | null;
  /** Estimated taker fee to close the whole position at the current mark.
   * Null when current price or fee rate unknown. */
  estClosingFeeCents: number | null;
  /** Live liquidation value = totalValueCents − estClosingFeeCents — what
   *  actually hits the account on a market sell. Null when current price
   *  or closing-fee rate is unknown. Gross value remains available separately
   *  in totalValueCents; never label a gross fallback as liquidation value. */
  cashOutCents: number | null;
  /** Net realized if the position settles WIN (100¢ payout, no settle fees). */
  ifWinCents: number;
  /** Live exit P&L: value − cost − estClosingFee. Null when the mark, basis
   * or closing fee is unavailable; never substitute a gross estimate. */
  unrealizedPnl: number | null;
  /** Why `unrealizedPnl` is null for a readable snapshot: the one concrete
   *  input that is missing, in the order the recipe needs them (mark, entry
   *  basis, closing-fee rate). Absent when the row is priced. */
  unpricedReason?: UnpricedReason;
}

/** Shared label copy for every surface that renders this hook's headline
 *  (live-tennis V1c): says what the number IS so a falling value while
 *  building two-sided inventory isn't misread as session quality. */
export const MTM_PNL_TOOLTIP =
  'Mark-to-market liquidation estimate: current mid × contracts − estimated taker close fee − (cost basis + entry fees). ' +
  'What a market close would roughly realize NOW — not paired/locked spread economics. ' +
  'Paired inventory context lives on the match card.';

export function usePositionPnl() {
  const positions = useAccountStore((s) => s.positions);
  // Snapshot evidence, NOT socket status (#881 review F4): a readable
  // positions snapshot must have been applied this session. Socket status is
  // wrong in both directions: 'connected' holds while every positions read
  // fails, and a retry-ladder 'connecting' does not invalidate holdings
  // already in hand. Marks come from the hydra feed and are gated on their
  // own validity below. No age deadline is applied here: `positionsSync`
  // carries only local receipt evidence, which is not a clock-domain-safe
  // basis for withholding.
  const positionsSync = useAccountStore((s) => s.positionsSync);
  const snapshotReadable = positionsSync.synced;
  // Throttled (4 Hz) market clock: this hook used to subscribe to the raw
  // ~30 Hz version AND rebuild a fresh array of fresh objects on every render
  // (bare .map, no memo) — poisoning every downstream useMemo keyed on its
  // output. Both consumers (PositionTracker, docked PositionSidebar) re-derive
  // at most 4×/s now, and referential identity holds between ticks.
  const version = useThrottledMarketVersion();
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  const fees = useFeeStore((s) => s.fees);
  const eventFees = useFeeStore((s) => s.eventFees);
  const feesReady = useFeeStore((s) => s.ready);
  const feesError = useFeeStore((s) => s.error);
  const feeSchedule = useFeeStore((s) => s.schedule);

  return useMemo(() => {
    // Force re-derive when the (throttled) Hydra clock advances
    void version;
    void fees;
    void eventFees;

    const positionsWithPnl: PositionWithPnl[] = positions.map((pos) => {
    // marketStore returns the YES mid; flip to side-native for NO so the
    // value compares directly against averagePrice (which is the actual
    // price paid for the side held — 72c on a NO contract means the user
    // paid 72c for that NO contract, not the YES-equivalent).
    const yesMid = snapshotReadable ? getMarketPrice(pos.ticker) : null;
    const currentPrice =
      yesMid == null || !Number.isFinite(yesMid) || yesMid < 0 || yesMid > 100
        ? null : pos.side === 'yes' ? yesMid : 100 - yesMid;

    // Open-share entry fees (openFeesCents), not the bucket total: composing
    // an unrealized number with whole-bucket feesPaidCents double-subtracts
    // the sold share's buy+sell fees (they belong to realized-net) on every
    // partially-closed position. Fall back to the bucket total only where the
    // relay didn't send the allocation (personal-key path) — degraded to the
    // old behavior, never to a silent $0. Same recipe as
    // computeOrgTraderUnrealized (OrgTradersTable).
    const feesPaid = pos.openFeesCents ?? pos.feesPaidCents ?? 0;
    // Prefer the relay's exact basis when present; avg × qty re-multiplies a
    // whole-cent-rounded avg and drifts on fractional/many-fill positions
    // (I-035). Personal-key positions (no relay ledger) keep the fallback.
    const entryCostCents = pos.costBasisCents ?? pos.averagePrice * pos.position;
    const totalCostCents = entryCostCents + feesPaid;
    const totalValueCents = currentPrice !== null ? currentPrice * pos.position : null;
    const ifWinCents = 100 * pos.position - totalCostCents;

    // Closing-fee estimate uses the taker rate (closer typically crosses
    // the spread). Symmetric in P × (1−P), so passing side-native cents
    // gives the same answer for YES vs NO. Uses the canonical aggregate
    // ceil — the fee Kalshi charges to close the whole position in one
    // execution, rounded up to the cent. No calibration coefficient: the
    // multi-fill premium it modeled does not exist.
    const takerRate = takerRateForTicker(pos.ticker);
    const estClosingFeeCents =
      currentPrice !== null && takerRate !== null && Number.isFinite(takerRate) && takerRate >= 0
        && Number.isFinite(pos.position) && pos.position > 0
        ? estimateAggregateFeeCents({
            priceCents: currentPrice,
            count: pos.position,
            feeRate: takerRate,
          })
        : null;

    const cashOutCents =
      totalValueCents !== null && Number.isFinite(totalValueCents)
        && estClosingFeeCents !== null && Number.isFinite(estClosingFeeCents)
        ? totalValueCents - estClosingFeeCents
        : null;
    // A row whose source carried no entry-basis evidence is a real holding
    // with an unknown cost: its P&L is unavailable (coverage counts it as
    // unpriced), never computed against the placeholder zero average.
    const unrealizedPnl =
      cashOutCents !== null && Number.isFinite(totalCostCents) && !pos.entryBasisUnknown
        ? cashOutCents - totalCostCents : null;

    // Name the missing input rather than leaving a bare null: the headline
    // tooltip and the priced subtotal read it. Snapshot-level withholding
    // (unreadable / failed read) is reported by the caller from positionsSync.
    let unpricedReason: UnpricedReason | undefined;
    if (unrealizedPnl === null && snapshotReadable) {
      if (currentPrice === null) unpricedReason = 'no-mark';
      else if (pos.entryBasisUnknown) unpricedReason = 'no-basis';
      else if (estClosingFeeCents === null) {
        const status = feeDisplayStatus(pos.ticker, feesReady, feeFor(pos.ticker), feeSchedule);
        unpricedReason = status === 'loading' ? (feesError ? 'fee-error' : 'fee-loading')
          : status === 'unsupported' ? 'fee-unsupported'
          : 'fee-missing';
      } else unpricedReason = 'no-basis';
    }

      return {
        ...pos,
        currentPrice,
        feesPaidCents: pos.feesPaidCents,
        totalCostCents,
        totalValueCents,
        estClosingFeeCents,
        cashOutCents,
        ifWinCents,
        unrealizedPnl,
        ...(unpricedReason ? { unpricedReason } : null),
      };
    });

    const coverage = summarizeUnrealizedCoverage(positionsWithPnl.map(p => p.unrealizedPnl));
    // An unreadable snapshot can neither present a complete headline nor a
    // false empty account: totalPnl is null until a readable snapshot exists.
    // After a readable snapshot, a KNOWN failed refresh (consecutiveFailures
    // > 0) withholds the account headline immediately: the rows keep their
    // last-good holdings and live marks, but the account total is no longer
    // evidence-complete. This is a failed-read fact, not an age deadline.
    const headlineComplete = snapshotReadable && positionsSync.consecutiveFailures === 0;
    // Stable sentence for the withheld-headline tooltip; null when every row is priced.
    const unpricedSummary = describeUnpricedReasons(positionsWithPnl.map(p => p.unpricedReason));
    return {
      positionsWithPnl,
      totalPnl: headlineComplete ? coverage.valueCents : null,
      coverage,
      positionsSync,
      unpricedSummary,
    };
  }, [positions, positionsSync, snapshotReadable, version, getMarketPrice, fees, eventFees, feesReady, feesError, feeSchedule]);

}
